import { DateTime } from "luxon";
import * as Yup from "yup";
import { Prisma } from "prisma-generated/browser";
import {
  type ConferenceDto,
  type ConferenceView,
  mapConferenceDtoToConferenceView,
  mapConferenceToConferenceDto,
} from "shared/mappers/database/conference/conference";
import {
  type ConferenceDayDto,
  type ConferenceDayView,
  mapConferenceDayDtoToConferenceDayView,
  mapConferenceDayToConferenceDayDto,
} from "shared/mappers/database/conference/conference-day";
import {
  type ConferenceEventFavoriteDto,
  type ConferenceEventFavoriteView,
  mapConferenceEventFavoriteDtoToConferenceEventFavoriteView,
  mapConferenceEventFavoriteToConferenceEventFavoriteDto,
} from "shared/mappers/database/conference/conference-event-favorite";
import {
  type ConferenceEventTemplateDto,
  type ConferenceEventTemplateView,
  mapConferenceEventTemplateDtoToConferenceEventTemplateView,
  mapConferenceEventTemplateToConferenceEventTemplateDto,
} from "shared/mappers/database/conference/conference-event-template";
import {
  type RefConferenceEventTypeDto,
  type RefConferenceEventTypeView,
  mapRefConferenceEventTypeDtoToRefConferenceEventTypeView,
  mapRefConferenceEventTypeToRefConferenceEventTypeDto,
} from "shared/mappers/database/conference/conference-event-type";
import {
  type ConferenceEventParticipantDto,
  type ConferenceEventParticipantView,
  mapConferenceEventParticipantDtoToConferenceEventParticipantView,
  mapConferenceEventParticipantToConferenceEventParticipantDto,
} from "shared/mappers/database/conference/conference-participant";
import {
  type ConferenceRoomDto,
  type ConferenceRoomView,
  mapConferenceRoomDtoToConferenceRoomView,
  mapConferenceRoomToConferenceRoomDto,
} from "shared/mappers/database/conference/conference-venue-room";
import {
  type ConferenceEventSubmissionInstanceView,
  mapConferenceEventSubmissionInstanceDtoToConferenceEventSubmissionInstanceView,
  mapConferenceEventSubmissionInstanceToConferenceEventSubmissionInstanceDto,
  type ConferenceEventSubmissionInstanceDto,
} from "shared/mappers/database/conference/event/submission/conference-event-submission-instance";
import {
  type CreatedModifiedDto,
  type CreatedModifiedView,
  mapCreatedModifiedDtoToCreatedModifiedView,
  mapCreatedModifiedToCreatedModifiedDto,
} from "shared/mappers/database/created-modified/created-modified";
import { mapIfExists } from "shared/mappers/database/mapIfExists";
import {
  type SocietyTagDto,
  type SocietyTagView,
  mapSocietyTagDtoToSocietyTagView,
  mapTagToSocietyTagDto,
} from "shared/mappers/database/tag/society-tag";
import {
  ConferenceEventCustomRelation,
  type PrismaConferenceEvent,
} from "shared/prisma/conference/conference-event";

export interface ConferenceEventDto {
  conferenceEventId: number;
  conference: ConferenceDto;
  parentEvent?: ConferenceEventDto;
  childEvents?: ConferenceEventDto[];
  conferenceDay?: ConferenceDayDto;
  eventType?: RefConferenceEventTypeDto;
  conferenceRoom?: ConferenceRoomDto;
  startTime?: DateTime;
  endTime?: DateTime;
  eventName?: string;
  comments?: string;
  created?: CreatedModifiedDto;
  lastModified?: CreatedModifiedDto;
  conferenceEventSubmissionInstances?: ConferenceEventSubmissionInstanceDto[];
  participants?: ConferenceEventParticipantDto[];
  eventTags?: SocietyTagDto[];
  everyTag?: SocietyTagDto[];
  someTag?: SocietyTagDto[];
  noneTag?: SocietyTagDto[];
  conferenceEventTemplate?: ConferenceEventTemplateDto[];
  additionalInfo?: ConferenceEventAdditionalInfo;
  priority?: number | null;
  conferenceEventFavorite?: ConferenceEventFavoriteDto[];
}

export interface ConferenceEventView {
  conferenceEventId: number;
  conference: ConferenceView;
  parentEvent?: ConferenceEventView;
  childEvents?: ConferenceEventView[];
  conferenceDay?: ConferenceDayView;
  eventType?: RefConferenceEventTypeView;
  conferenceRoom?: ConferenceRoomView;
  startTime?: string;
  endTime?: string;
  eventName?: string;
  comments?: string;
  created?: CreatedModifiedView;
  lastModified?: CreatedModifiedView;
  conferenceEventSubmissionInstances?: ConferenceEventSubmissionInstanceView[];
  participants?: ConferenceEventParticipantView[];
  eventTags?: SocietyTagView[];
  everyTag?: SocietyTagView[];
  someTag?: SocietyTagView[];
  noneTag?: SocietyTagView[];
  conferenceEventTemplate?: ConferenceEventTemplateView[];
  additionalInfo?: ConferenceEventAdditionalInfo;
  priority?: number | null;
  conferenceEventFavorite?: ConferenceEventFavoriteView[];
}

export interface ConferenceEventAdditionalInfo extends Prisma.JsonObject {
  sequenceNumber?: number;
}

const additionalInfoSchema = Yup.object({ sequenceNumber: Yup.number() });

const isAdditionalInfo = (
  value: Prisma.JsonValue | undefined,
): value is ConferenceEventAdditionalInfo => {
  if (value == null || typeof value !== "object") {
    return false;
  }
  return additionalInfoSchema.isValidSync(value);
};

export const mapConferenceEventToConferenceEventDto = (
  entity: PrismaConferenceEvent,
) => {
  const conferenceDto = mapConferenceToConferenceDto(entity.Conference);

  const conferenceEventDto: ConferenceEventDto = {
    conferenceEventId: entity.id,
    eventName: entity.event_name,
    parentEvent: mapIfExists(
      entity,
      ConferenceEventCustomRelation.ParentEvent,
      mapConferenceEventToConferenceEventDto,
    ),
    childEvents: mapIfExists(
      entity,
      ConferenceEventCustomRelation.ChildEvents,
      mapConferenceEventToConferenceEventDto,
    ),
    conferenceDay: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceDay,
      mapConferenceDayToConferenceDayDto,
    ),
    eventType: mapIfExists(
      entity,
      Prisma.ModelName.RefConferenceEventType,
      mapRefConferenceEventTypeToRefConferenceEventTypeDto,
    ),
    conferenceRoom: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceRoom,
      mapConferenceRoomToConferenceRoomDto,
    ),
    startTime: entity.start_time
      ? DateTime.fromJSDate(entity.start_time, { zone: "utc" }).setZone(
          conferenceDto.conferenceVenue.timeZone,
        )
      : undefined,
    endTime: entity.end_time
      ? DateTime.fromJSDate(entity.end_time, { zone: "utc" }).setZone(
          conferenceDto.conferenceVenue.timeZone,
        )
      : undefined,
    comments: entity.comments,
    conference: conferenceDto,
    created: mapIfExists(
      entity,
      "CreatedModified_ConferenceEvent_created_idToCreatedModified",
      mapCreatedModifiedToCreatedModifiedDto,
    ),
    lastModified: mapIfExists(
      entity,
      "CreatedModified_ConferenceEvent_last_modified_idToCreatedModified",
      mapCreatedModifiedToCreatedModifiedDto,
    ),
    conferenceEventSubmissionInstances: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventSubmissionInstance,
      mapConferenceEventSubmissionInstanceToConferenceEventSubmissionInstanceDto,
    ),
    participants: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventParticipant,
      mapConferenceEventParticipantToConferenceEventParticipantDto,
    ),
    eventTags: mapIfExists(entity, Prisma.ModelName.ConferenceEventTag, (tag) =>
      mapIfExists(tag, Prisma.ModelName.SocietyTag, mapTagToSocietyTagDto),
    ),
    everyTag: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventTagEvery,
      (tag) =>
        mapIfExists(tag, Prisma.ModelName.SocietyTag, mapTagToSocietyTagDto),
    ),
    someTag: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventTagSome,
      (tag) =>
        mapIfExists(tag, Prisma.ModelName.SocietyTag, mapTagToSocietyTagDto),
    ),
    noneTag: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventTagNone,
      (tag) =>
        mapIfExists(tag, Prisma.ModelName.SocietyTag, mapTagToSocietyTagDto),
    ),
    conferenceEventTemplate: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventTemplate,
      mapConferenceEventTemplateToConferenceEventTemplateDto,
    ),
    additionalInfo: isAdditionalInfo(entity.additional_info)
      ? entity.additional_info
      : undefined,
    priority: entity.priority,
    conferenceEventFavorite: mapIfExists(
      entity,
      Prisma.ModelName.ConferenceEventFavorite,
      mapConferenceEventFavoriteToConferenceEventFavoriteDto,
    ),
  };

  return conferenceEventDto;
};

export const mapConferenceEventDtoToConferenceEventView = (
  dto: ConferenceEventDto,
) => {
  const conferenceEventView: ConferenceEventView = {
    conferenceEventId: dto.conferenceEventId,
    eventName: dto.eventName,
    comments: dto.comments,
    parentEvent: dto.parentEvent
      ? mapConferenceEventDtoToConferenceEventView(dto.parentEvent)
      : undefined,
    childEvents:
      dto.childEvents?.map(mapConferenceEventDtoToConferenceEventView) ?? [],
    conferenceDay: dto.conferenceDay
      ? mapConferenceDayDtoToConferenceDayView(dto.conferenceDay)
      : undefined,
    eventType: dto.eventType
      ? mapRefConferenceEventTypeDtoToRefConferenceEventTypeView(dto.eventType)
      : undefined,
    conferenceRoom: dto.conferenceRoom
      ? mapConferenceRoomDtoToConferenceRoomView(dto.conferenceRoom)
      : undefined,
    startTime: dto.startTime?.toISO() ?? undefined,
    endTime: dto.endTime?.toISO() ?? undefined,
    conference: mapConferenceDtoToConferenceView(dto.conference),
    created: dto.created
      ? mapCreatedModifiedDtoToCreatedModifiedView(dto.created)
      : undefined,
    lastModified: dto.lastModified
      ? mapCreatedModifiedDtoToCreatedModifiedView(dto.lastModified)
      : undefined,
    conferenceEventSubmissionInstances:
      dto.conferenceEventSubmissionInstances?.map(
        mapConferenceEventSubmissionInstanceDtoToConferenceEventSubmissionInstanceView,
      ),
    participants: dto.participants?.map(
      mapConferenceEventParticipantDtoToConferenceEventParticipantView,
    ),
    eventTags: dto.eventTags?.map(mapSocietyTagDtoToSocietyTagView),
    everyTag: dto.everyTag?.map(mapSocietyTagDtoToSocietyTagView),
    someTag: dto.someTag?.map(mapSocietyTagDtoToSocietyTagView),
    noneTag: dto.noneTag?.map(mapSocietyTagDtoToSocietyTagView),
    conferenceEventTemplate: dto.conferenceEventTemplate?.map(
      mapConferenceEventTemplateDtoToConferenceEventTemplateView,
    ),
    additionalInfo: dto.additionalInfo,
    priority: dto.priority,
    conferenceEventFavorite: dto.conferenceEventFavorite?.map(
      mapConferenceEventFavoriteDtoToConferenceEventFavoriteView,
    ),
  };
  return conferenceEventView;
};
